import type { PatientAction, PatientActionCheckIn, StaffTitle, TimelineEntry } from "@ferrivia/api-contract";
import { currentCheckIn, isActive, activeActions } from "./plan";

/* The Journey tab's care log: one union over staff-authored records and the
   timeline's domain facts. Everything is ordered by a date a human set or by
   when the record was written, never by anything about a client's values. */

export interface ComingUpItem {
  action: PatientAction;
  on: string;
  channel: PatientAction["checkInChannel"];
  setBy: { name: string; title: StaffTitle; at: string };
}

export type HistoryItem =
  | { kind: "action"; id: string; occurredAt: string; action: PatientAction; previous?: PatientAction; active: boolean; replacedAt?: string }
  | { kind: "action_check_in"; id: string; occurredAt: string; checkIn: PatientActionCheckIn; action: PatientAction }
  | { kind: "timeline"; id: string; occurredAt: string; entry: TimelineEntry };

export interface CareLog {
  comingUp: ComingUpItem[];
  history: HistoryItem[];
}

export function buildCareLog(input: {
  actions: PatientAction[];
  checkIns: PatientActionCheckIn[];
  timelineEntries: TimelineEntry[];
  today: string;
}): CareLog {
  const { actions, checkIns, timelineEntries, today } = input;
  const byId = new Map(actions.map((a) => [a.id, a]));
  const successorOf = new Map(actions.filter((a) => a.replacesId).map((a) => [a.replacesId as string, a]));

  const comingUp: ComingUpItem[] = [];
  for (const action of activeActions(actions, checkIns)) {
    const { on, channel } = currentCheckIn(action, checkIns);
    if (on === null || on < today) {
      continue;
    }
    const continued = checkIns
      .filter((c) => c.actionId === action.id && c.outcome === "continue")
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
    const source = continued ?? action;
    comingUp.push({
      action,
      on,
      channel,
      setBy: { name: source.authorName, title: source.authorTitle, at: source.createdAt },
    });
  }

  const history: HistoryItem[] = [
    ...actions.map(
      (action): HistoryItem => ({
        kind: "action",
        id: `action:${action.id}`,
        occurredAt: action.createdAt,
        action,
        previous: action.replacesId ? byId.get(action.replacesId) : undefined,
        active: isActive(action, actions, checkIns),
        replacedAt: successorOf.get(action.id)?.createdAt,
      }),
    ),
    ...checkIns.flatMap((checkIn): HistoryItem[] => {
      const action = byId.get(checkIn.actionId);
      return action
        ? [{ kind: "action_check_in", id: `action_check_in:${checkIn.id}`, occurredAt: checkIn.createdAt, checkIn, action }]
        : [];
    }),
    ...timelineEntries.map(
      (entry): HistoryItem => ({ kind: "timeline", id: `timeline:${entry.id}`, occurredAt: entry.occurredAt, entry }),
    ),
  ];
  history.sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime());

  return { comingUp, history };
}
